/**
 * Thin wrapper around Exotel's CRM Web SDK (the browser softphone used for
 * Web Calling / IP-PSTN intermix).
 *
 * Everything Exotel-specific is contained here so the Vuex module and the
 * widget component only ever deal with normalised state. Three behaviours are
 * worth calling out, because they compensate for gaps in the SDK itself:
 *
 *  1. The SDK is loaded lazily. It pulls in a full WebRTC stack and is useless
 *     to accounts that have not switched Web Calling on, so it must not sit in
 *     the main dashboard bundle.
 *
 *  2. The SDK never reports a failed registration — its client only emits
 *     "registered" and "unregistered" — so a bad token or a firewall blocking
 *     the signalling socket looks exactly like nothing happening. We time the
 *     attempt out ourselves and surface a real failure.
 *
 *  3. Registration is leased across browser tabs. Agents keep several Chatwoot
 *     tabs open; registering the same SIP user in each one means an incoming
 *     call rings in whichever tab the SIP server happens to pick. Exactly one
 *     tab holds the lease at a time.
 */

import {
  SDK_CALL_EVENTS,
  SDK_REGISTRATION_EVENTS,
  REGISTRATION_STATES,
  CALL_STATES,
  CALL_DIRECTIONS,
  REGISTRATION_TIMEOUT,
  PHONE_LEASE_KEY,
  PHONE_LEASE_TTL,
  PHONE_LEASE_HEARTBEAT,
  PHONE_AVAILABILITY_KEY,
} from 'dashboard/constants/calling';

let sdkModulePromise = null;

// Loaded on demand and cached, so switching accounts or re-registering does
// not re-download the chunk.
const loadSdkModule = () => {
  if (!sdkModulePromise) {
    sdkModulePromise = import(
      /* webpackChunkName: "exotel-web-phone" */
      '@exotel-npm-dev/exotel-ip-calling-crm-websdk'
    );
  }
  return sdkModulePromise;
};

const now = () => new Date().getTime();

const availabilityKey = userId => `${PHONE_AVAILABILITY_KEY}:${userId}`;

/**
 * An agent is available unless they have said otherwise, so anything we cannot
 * read — no stored value, blocked site data — means available. Signing off is
 * the deliberate act; the default must never leave a phone silently dark.
 */
export const readAvailability = userId => {
  try {
    const stored = window.localStorage.getItem(availabilityKey(userId));
    return stored === null ? true : JSON.parse(stored) !== false;
  } catch (error) {
    return true;
  }
};

export const writeAvailability = (userId, isAvailable) => {
  try {
    window.localStorage.setItem(
      availabilityKey(userId),
      JSON.stringify(!!isAvailable)
    );
  } catch (error) {
    // Unwritable storage costs the agent their choice on the next reload, but
    // the switch still applies for this session.
  }
};

const readLease = () => {
  try {
    return JSON.parse(window.localStorage.getItem(PHONE_LEASE_KEY)) || null;
  } catch (error) {
    return null;
  }
};

const writeLease = lease => {
  try {
    window.localStorage.setItem(PHONE_LEASE_KEY, JSON.stringify(lease));
  } catch (error) {
    // Storage can be unavailable (private browsing, blocked site data). We
    // fall back to letting this tab register, which is the pre-lease
    // behaviour, rather than leaving the agent with no softphone at all.
  }
};

/**
 * Normalises the SDK's call payload into the shape the store consumes.
 * `remoteId` is a SIP URI for inbound calls and the dialled number for
 * outbound ones, so the display name is preferred where the SDK provides it.
 */
const normalizeCall = (event, callData = {}) => {
  const {
    callId,
    remoteId,
    remoteDisplayName,
    callDirection,
    callDuration,
    callFromNumber,
    sessionId,
    callEndReason,
  } = callData;

  const isInbound = String(callDirection || '').toLowerCase() !== 'outbound';

  return {
    callId,
    sessionId,
    direction: isInbound ? CALL_DIRECTIONS.INBOUND : CALL_DIRECTIONS.OUTBOUND,
    // For an inbound call the customer's number arrives as callFromNumber;
    // remoteId is the SIP address, which is not something to show an agent.
    remoteNumber: isInbound ? callFromNumber || remoteId : remoteId,
    remoteName: remoteDisplayName || '',
    duration: callDuration,
    endReason: callEndReason,
    event,
  };
};

class ExotelWebPhone {
  constructor() {
    this.phone = null;
    this.tabId = `${now()}-${Math.random().toString(36).slice(2)}`;
    this.leaseTimer = null;
    this.registrationTimer = null;
    this.handlers = {};
    this.identity = null;
    // Distinguishes a failed registration from a deliberate sign off, which
    // the SDK reports with the same "unregistered" event.
    this.hasRegisteredOnce = false;
  }

  get isConnected() {
    return !!this.phone;
  }

  /**
   * Claims the cross-tab registration lease. Returns false when another tab
   * already holds a lease that has not gone stale, in which case this tab
   * renders the widget in a read-only "active in another tab" state.
   */
  claimLease() {
    const lease = readLease();
    const isHeldElsewhere =
      lease &&
      lease.tabId !== this.tabId &&
      now() - lease.updatedAt < PHONE_LEASE_TTL;

    if (isHeldElsewhere) return false;

    writeLease({ tabId: this.tabId, updatedAt: now() });
    return true;
  }

  startLeaseHeartbeat() {
    this.stopLeaseHeartbeat();
    this.leaseTimer = setInterval(() => {
      writeLease({ tabId: this.tabId, updatedAt: now() });
    }, PHONE_LEASE_HEARTBEAT);
  }

  stopLeaseHeartbeat() {
    if (this.leaseTimer) clearInterval(this.leaseTimer);
    this.leaseTimer = null;
  }

  releaseLease() {
    const lease = readLease();
    if (lease && lease.tabId === this.tabId) {
      try {
        window.localStorage.removeItem(PHONE_LEASE_KEY);
      } catch (error) {
        // Nothing to do — the lease will go stale on its own within the TTL.
      }
    }
  }

  /**
   * Boots the softphone and registers the device.
   *
   * @param {string} accessToken Token issued by Exotel for the VoIP account.
   * @param {string} userId      The agent's Exotel user id (their email).
   * @param {Function} onCallEvent        Receives normalised call payloads.
   * @param {Function} onRegistrationEvent Receives a REGISTRATION_STATES value.
   * @param {boolean} autoRegister  False for an agent who has signed off: the
   *   SDK is booted (so the switch works without a reload) but the device is
   *   left unregistered, and no call can ring.
   */
  async connect({
    accessToken,
    userId,
    onCallEvent,
    onRegistrationEvent,
    autoRegister = true,
  }) {
    if (this.phone) return this.phone;

    this.handlers = { onCallEvent, onRegistrationEvent };
    this.identity = { accessToken, userId };

    onRegistrationEvent(
      autoRegister
        ? REGISTRATION_STATES.REGISTERING
        : REGISTRATION_STATES.UNREGISTERED
    );

    let SdkConstructor = null;
    try {
      const sdkModule = await loadSdkModule();
      SdkConstructor = sdkModule.default || sdkModule.ExotelCRMWebSDK;
    } catch (error) {
      onRegistrationEvent(REGISTRATION_STATES.FAILED, error);
      return null;
    }

    try {
      // autoConnectVOIP is left off so registration is an explicit step we can
      // time out; the SDK would otherwise register during Initialize with no
      // way for us to know whether it succeeded.
      const sdk = new SdkConstructor(accessToken, userId, false);
      const phone = await sdk.Initialize(
        this.handleCallEvent,
        this.handleRegistrationEvent
      );

      // Initialize resolves to undefined when it cannot load the app, the app
      // settings, or the user mapping for this agent — the usual cause being
      // an agent who exists in Chatwoot but was never provisioned in Exotel.
      if (!phone) {
        onRegistrationEvent(
          REGISTRATION_STATES.FAILED,
          new Error(
            'Exotel SDK could not load this agent — check the browser console for [crm-websdk] errors'
          )
        );
        return null;
      }

      this.phone = phone;
      // The lease is held for as long as this tab owns the softphone, whether
      // or not it is registered: an agent who is signed off still owns the
      // phone, and must not have it taken over by another tab mid-toggle.
      this.startLeaseHeartbeat();
      if (autoRegister) this.register();
      return phone;
    } catch (error) {
      onRegistrationEvent(REGISTRATION_STATES.FAILED, error);
      return null;
    }
  }

  /**
   * Registers the device with Exotel's call server — the agent starts ringing.
   * Returns false when there is no softphone to register, so the caller can
   * tell "switched on" from "nothing to switch on".
   */
  register() {
    if (!this.phone) return false;
    this.startRegistrationTimeout();
    this.phone.RegisterDevice();
    return true;
  }

  /**
   * Removes the device from the call server. Incoming calls stop arriving;
   * the SDK, the lease and any call in progress are left alone.
   */
  unregister() {
    if (!this.phone) return false;
    this.clearRegistrationTimeout();
    // From here on an "unregistered" event is this, not a rejected
    // registration — including when the agent signs off before the first
    // REGISTER has been answered.
    this.hasRegisteredOnce = true;
    try {
      this.phone.UnRegisterDevice();
    } catch (error) {
      // Already gone — the agent wanted the phone off, and it is off.
    }
    return true;
  }

  disconnect() {
    this.clearRegistrationTimeout();
    this.stopLeaseHeartbeat();
    this.hasRegisteredOnce = false;
    try {
      this.phone?.UnRegisterDevice();
    } catch (error) {
      // The SDK throws if the socket is already gone; the agent is leaving
      // either way, so there is nothing useful to recover here.
    }
    this.releaseLease();
    this.phone = null;
  }

  startRegistrationTimeout() {
    this.clearRegistrationTimeout();
    this.registrationTimer = setTimeout(() => {
      this.handlers.onRegistrationEvent?.(
        REGISTRATION_STATES.FAILED,
        new Error(
          'Softphone did not register within 15s — the connection to voip.in1.exotel.com may be blocked'
        )
      );
    }, REGISTRATION_TIMEOUT);
  }

  clearRegistrationTimeout() {
    if (this.registrationTimer) clearTimeout(this.registrationTimer);
    this.registrationTimer = null;
  }

  handleRegistrationEvent = state => {
    const normalized = String(state || '').toLowerCase();

    if (normalized === SDK_REGISTRATION_EVENTS.REGISTERED) {
      this.clearRegistrationTimeout();
      this.hasRegisteredOnce = true;
      this.handlers.onRegistrationEvent?.(REGISTRATION_STATES.REGISTERED);
      return;
    }

    if (normalized === SDK_REGISTRATION_EVENTS.UNREGISTERED) {
      // The SDK cannot tell us why it unregistered. Before the phone has ever
      // come up this is a failure to connect — a bad token, a blocked
      // signalling socket, an agent missing from Exotel — and the agent needs
      // to know. Afterwards it is just the device going offline. The client
      // retries on its own, so a transient failure flips back to registered.
      if (this.hasRegisteredOnce) {
        this.handlers.onRegistrationEvent?.(REGISTRATION_STATES.UNREGISTERED);
        return;
      }
      this.handlers.onRegistrationEvent?.(
        REGISTRATION_STATES.FAILED,
        new Error('Exotel rejected the SIP registration')
      );
    }
  };

  handleCallEvent = (event, callData) => {
    const call = normalizeCall(event, callData);

    switch (event) {
      case SDK_CALL_EVENTS.INCOMING:
        this.handlers.onCallEvent?.({ ...call, state: CALL_STATES.INCOMING });
        break;
      case SDK_CALL_EVENTS.CONNECTED:
        this.handlers.onCallEvent?.({ ...call, state: CALL_STATES.CONNECTED });
        break;
      case SDK_CALL_EVENTS.CALL_ENDED:
        this.handlers.onCallEvent?.({ ...call, state: CALL_STATES.IDLE });
        break;
      case SDK_CALL_EVENTS.HOLD_TOGGLE:
      case SDK_CALL_EVENTS.MUTE_TOGGLE:
        this.handlers.onCallEvent?.({ ...call, toggle: event });
        break;
      default:
        break;
    }
  };

  acceptCall() {
    this.phone?.AcceptCall();
  }

  hangupCall() {
    this.phone?.HangupCall();
  }

  toggleHold() {
    this.phone?.ToggleHold();
  }

  toggleMute() {
    this.phone?.ToggleMute();
  }

  sendDtmf(digit) {
    this.phone?.SendDTMF(digit);
  }

  /**
   * Places an outbound call. Resolves with whatever Exotel returns for the
   * dial request, which is the only handle we get on the call: the SDK's
   * MakeCall takes no custom field, despite what its README claims, so this
   * payload is the sole means of tying an outbound call back to a
   * conversation.
   */
  makeCall(number) {
    return new Promise((resolve, reject) => {
      if (!this.phone) {
        reject(new Error('Softphone is not registered'));
        return;
      }
      this.phone.MakeCall(number, (status, data) => {
        if (status === 'success') resolve(data);
        else reject(data instanceof Error ? data : new Error('Dial failed'));
      });
    });
  }
}

export default new ExotelWebPhone();
